import {get, post} from '@/services/infrastructure/api.service'
import {PERIOD_CLOSED, PERIOD_OPENED} from '@/types/periodLock/periodLockEvent'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {onMounted, ref} from 'vue'
import {ORG_SELECTED, ORG_SWITCHED} from '@/types/events'

const {onEvents} = useEmitter()

export const periods = ref<Period[]>([])
export const periodsLoaded = ref(false)

const getPeriods = async (tenantId?: string): Promise<Period[]> => {
    return await get('/periods', {tenantId: tenantId}) ?? []
}


export const closePeriod = async (monthYear: MonthYear) => {
    const period = await post('/periods/close', {
        month: monthYear.month + 1,
        year: monthYear.year,
    })
    await updateClosedPeriod(period)
    emitter.emit(PERIOD_CLOSED)
}

export const closePeriods = async (fromMonthYear: MonthYear, toMonthYear: MonthYear) => {
    const periods = await post('/periods/close/batch', {fromMonth: fromMonthYear.month+1, fromYear:fromMonthYear.year, toMonth: toMonthYear.month+1, toYear: toMonthYear.year})
    await Promise.all(periods.map(updateClosedPeriod))
    emitter.emit(PERIOD_CLOSED)
}

export const openPeriod = async (periodUuid: string, comment: string) => {
    await post('/periods/open', {
        periodUuid: periodUuid,
        comment: comment
    })
    await updateOpenedPeriod(periodUuid)
    emitter.emit(PERIOD_OPENED)
}

const updateClosedPeriod = async (updatedPeriod: Period) => {
    if (updatedPeriod) {
        const found = periods.value.some(p => p.uuid === updatedPeriod.uuid)
        if (found) {
            periods.value = periods.value.map(p => {
                if (p.uuid === updatedPeriod.uuid) {
                    return {
                        ...p,
                        isClosed: true,
                        updatedAt: new Date(),
                        latestComment: updatedPeriod.latestComment
                    }
                }
                return p
            })
        } else {
            periods.value = [...periods.value, updatedPeriod]
        }
    }
}

const updateOpenedPeriod = async (periodUuid: string) => {
    periods.value = periods.value.map(p => {
        if (p.uuid === periodUuid) {
            return {
                ...p,
                isClosed: false,
                updatedAt: new Date(),
            }
        }
        return p
    })
}

let initialized = false
const init = async () => {
    if (initialized) return
    initialized = true
    periodsLoaded.value = false
    await fetchAllPeriods()
}

export const fetchAllPeriods = async () => {
    try {
        periods.value = await getPeriods()
        periodsLoaded.value = true
    } catch (e: any) {
        console.error('Error fetching periods: ', e)
        periodsLoaded.value = false
        return []
    }
}

export const fetchPeriodLogs = async (periodUuid: string): Promise<Log[]> => {
    return await get<Log[]>(`/periods/${periodUuid}/logs`) ?? []
}

onMounted(async () => await init())

onEvents([ORG_SELECTED, ORG_SWITCHED, PERIOD_CLOSED, PERIOD_OPENED], async () => {
    await fetchAllPeriods()
})

export type Period = {
    uuid: string,
    month: number,
    year: number,
    isClosed: boolean,
    latestComment?: string,
    updatedAt: Date,
    user: string
}

export type PeriodInitOpen = {
    month: number,
    year: number,
    isClosed: boolean,
}

export type Log = {
    comment?: string,
    action: string,
    createdAt: string,
}

export type MonthYear = {
    month: number
    year: number
}